import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import Dialog from '@/components/ui/Dialog/Dialog'
import type { DialogProps } from '@/components/ui/Dialog/Dialog'
import Button from '@/components/ui/Button/Button'
import { Alert } from '@/components/ui/Alert'
import Steps from '@/components/ui/Steps'
import Spinner from '@/components/ui/Spinner'

import { useMasterPasswordStore } from '@/store/masterPasswordStore'
import {
    initSodium,
    generateNoteKey,
    wrapNoteKey,
    zeroize,
} from '@/services/CryptoService'
import { apiStoreNoteKeyWrapper } from '@/services/NoteKeyWrapperService'
import { triggerErrorMessage } from '@/components/ui/toast'

// ---------------------------------------------------------------------------
// Step types  (mirrors MasterPasswordSetupModal patterns)
// ---------------------------------------------------------------------------

type StepStatus = 'pending' | 'in-progress' | 'complete' | 'error'

type WrapperStep = {
    key: 'generateKey' | 'wrapKey' | 'save'
    labelKey: string
    status: StepStatus
}

function toStepsStatus(s: StepStatus) {
    switch (s) {
        case 'complete':    return 'complete'    as const
        case 'error':       return 'error'       as const
        case 'in-progress': return 'in-progress' as const
        default:            return 'pending'     as const
    }
}

function currentIndex(steps: WrapperStep[]): number {
    const idx = steps.findIndex(
        (s) => s.status === 'in-progress' || s.status === 'error',
    )
    if (idx !== -1) return idx
    if (steps.every((s) => s.status === 'complete')) return steps.length
    return 0
}

const INITIAL_STEPS: WrapperStep[] = [
    { key: 'generateKey', labelKey: 'noteKeyWrapper.stepGenerateKey', status: 'pending' },
    { key: 'wrapKey',     labelKey: 'noteKeyWrapper.stepWrapKey',     status: 'pending' },
    { key: 'save',        labelKey: 'noteKeyWrapper.stepSave',        status: 'pending' },
]

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface NoteKeyWrapperProgressModalProps {
    isOpen: boolean
    noteId: string
    masterKey: Uint8Array
    /** Called with the live noteKey on success. Caller takes ownership and must zeroize it. */
    onSuccess: (noteKey: Uint8Array) => void
    /** Called when the user cancels or the modal is dismissed without success. */
    onCancel: () => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when an Axios error looks like a crypto / auth failure. */
function isCryptoOrAuthError(err: unknown): boolean {
    const status = (err as AxiosError)?.response?.status
    return status === 401 || status === 403
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const NoteKeyWrapperProgressModal = ({
    isOpen,
    noteId,
    masterKey,
    onSuccess,
    onCancel,
}: NoteKeyWrapperProgressModalProps) => {
    const { t } = useTranslation()

    const clearMasterKey = useMasterPasswordStore((s) => s.clearMasterKey)

    const [steps, setSteps]         = useState<WrapperStep[]>(INITIAL_STEPS)
    const [stepError, setStepError] = useState<string | null>(null)
    const [running, setRunning]     = useState(false)

    // Ref lets the abort-on-cancel check read the latest value without
    // needing it as a dep of the run callback.
    const cancelledRef = useRef(false)

    // -----------------------------------------------------------------------
    // Step runner
    // -----------------------------------------------------------------------

    const updateStep = useCallback(
        (key: WrapperStep['key'], status: StepStatus) => {
            setSteps((prev) =>
                prev.map((s) => (s.key === key ? { ...s, status } : s)),
            )
        },
        [],
    )

    const run = useCallback(async () => {
        cancelledRef.current = false
        setSteps(INITIAL_STEPS.map((s) => ({ ...s, status: 'pending' })))
        setStepError(null)
        setRunning(true)

        // Yield so the pending state renders before any blocking work.
        await new Promise((r) => setTimeout(r, 0))

        // Helper: defer a blocking synchronous call so the spinner renders first.
        const defer = <T,>(fn: () => T): Promise<T> =>
            new Promise((resolve, reject) =>
                setTimeout(() => {
                    try { resolve(fn()) } catch (e) { reject(e) }
                }, 0),
            )

        let noteKey: Uint8Array | null = null

        try {
            // -- Step 1: Generate note key --
            updateStep('generateKey', 'in-progress')
            await initSodium()

            if (cancelledRef.current) { setRunning(false); return }

            noteKey = await defer(() => generateNoteKey())
            updateStep('generateKey', 'complete')

            // -- Step 2: Wrap note key with master key --
            updateStep('wrapKey', 'in-progress')

            if (cancelledRef.current) {
                zeroize(noteKey); noteKey = null
                setRunning(false); return
            }

            const payload = await defer(() => wrapNoteKey(noteKey!, masterKey))
            updateStep('wrapKey', 'complete')

            // -- Step 3: Save to backend --
            updateStep('save', 'in-progress')

            if (cancelledRef.current) {
                zeroize(noteKey); noteKey = null
                setRunning(false); return
            }

            try {
                await apiStoreNoteKeyWrapper(noteId, payload)
            } catch (err) {
                const status = (err as AxiosError)?.response?.status

                if (status === 409) {
                    // Wrapper already exists — treat as success but the locally
                    // generated key is NOT the authoritative key; zeroize it.
                    zeroize(noteKey)
                    noteKey = null
                    updateStep('save', 'complete')
                    await new Promise((r) => setTimeout(r, 600))
                    setRunning(false)
                    onSuccess(new Uint8Array(0)) // signal success without a usable key
                    return
                }

                // Network / server error (non-auth)
                if (isCryptoOrAuthError(err)) {
                    clearMasterKey()
                    triggerErrorMessage(t('noteKeyWrapper.errorAuth'))
                } else {
                    triggerErrorMessage(t('noteKeyWrapper.errorSave'))
                }

                updateStep('save', 'error')
                setStepError(
                    isCryptoOrAuthError(err)
                        ? t('noteKeyWrapper.errorAuth')
                        : t('noteKeyWrapper.errorSave'),
                )
                zeroize(noteKey)
                noteKey = null
                setRunning(false)
                return
            }

            updateStep('save', 'complete')

            // Small delay so the user sees all steps green before close.
            await new Promise((r) => setTimeout(r, 600))

            if (cancelledRef.current) {
                // Cancelled after save — key is persisted but we still abort the insert.
                zeroize(noteKey); noteKey = null
                setRunning(false); return
            }

            setRunning(false)
            // Transfer key ownership to caller.
            onSuccess(noteKey)
            noteKey = null

        } catch (err) {
            // noteKey is always null here — every non-null branch above already
            // zeroizes it before re-throwing or returning. Belt-and-suspenders reset.
            noteKey = null

            if (isCryptoOrAuthError(err)) {
                clearMasterKey()
                triggerErrorMessage(t('noteKeyWrapper.errorAuth'))
                setStepError(t('noteKeyWrapper.errorAuth'))
            } else {
                triggerErrorMessage(t('noteKeyWrapper.errorGeneric'))
                setStepError(t('noteKeyWrapper.errorGeneric'))
            }

            // Mark whichever step was in-progress as failed.
            setSteps((prev) =>
                prev.map((s) =>
                    s.status === 'in-progress' ? { ...s, status: 'error' } : s,
                ),
            )
            setRunning(false)
        }
    }, [noteId, masterKey, onSuccess, clearMasterKey, updateStep, t])

    // Start running automatically when the modal opens.
    useEffect(() => {
        if (isOpen) {
            void run()
        }
    }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps
    // `run` is intentionally excluded — we only want to trigger on open.

    // -----------------------------------------------------------------------
    // Handlers
    // -----------------------------------------------------------------------

    const handleCancel = () => {
        cancelledRef.current = true
        onCancel()
    }

    const handleRetry = () => {
        void run()
    }

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    const dialogProps = {
            isOpen,
            onRequestClose: handleCancel,
            shouldCloseOnOverlayClick: false,
            shouldCloseOnEsc: !running,
            onClose: handleCancel,
            closable: false,
            width: 420,
            aria: {
                labelledby: 'nkw-title',
                describedby: 'nkw-description',
            },
        } as DialogProps

    return (
        <Dialog {...dialogProps}>
            <div className="px-1">
                <h2
                    id="nkw-title"
                    className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-0.5"
                >
                    {t('noteKeyWrapper.title')}
                </h2>
                <p
                    id="nkw-description"
                    className="text-xs text-gray-500 dark:text-gray-400 mb-4"
                >
                    {t('noteKeyWrapper.subtitle')}
                </p>

                <div className="py-2">
                    <Steps vertical current={currentIndex(steps)}>
                        {steps.map((step) => (
                            <Steps.Item
                                key={step.key}
                                title={t(step.labelKey)}
                                status={toStepsStatus(step.status)}
                                customIcon={
                                    step.status === 'in-progress'
                                        ? <Spinner size={16} />
                                        : undefined
                                }
                            />
                        ))}
                    </Steps>
                </div>

                {stepError && (
                    <div className="mt-3 space-y-3">
                        <Alert type="danger" showIcon>
                            {stepError}
                        </Alert>
                        <div className="flex justify-end gap-2">
                            <Button variant="plain" onClick={handleCancel}>
                                {t('noteKeyWrapper.cancelButton')}
                            </Button>
                            <Button variant="solid" onClick={handleRetry}>
                                {t('noteKeyWrapper.retryButton')}
                            </Button>
                        </div>
                    </div>
                )}

                {!stepError && (
                    <div className="flex justify-end mt-4">
                        <Button
                            variant="plain"
                            disabled={!running}
                            onClick={handleCancel}
                        >
                            {t('noteKeyWrapper.cancelButton')}
                        </Button>
                    </div>
                )}
            </div>
        </Dialog>
    )
}

export default NoteKeyWrapperProgressModal

